import { network } from "../config";
import {
  Course,
  CourseFileTypes,
  CoursesParamsAvailable,
  LectureFiles,
  TypeCoursesFilters,
} from "../types/Courses";
import TypeDefaultResponse from "../types/TypeDefaultResponse";
import { TypeUpdatedUserInfo } from "../types/TypeUsers";
import url from "../utils/formApiUrl";
import customKy from "./kyExtend";

const baseURL = process.env.REACT_APP_HTTP_CONNECTION_STRING + "courses/";

const getAuthHeader = (token: string) => ({
  Authorization: `Bearer ${token}`,
});

const { courses } = network;

export default class CoursesApi {
  static async getCourseById(
    token: string,
    id: string,
  ): Promise<Course | null> {
    return await customKy
      .get(url(baseURL, courses.getCourseById, { id }), {
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async create(
    token: string,
    payload: {
      newCourse: Pick<Course, "title" | "language">;
    },
  ): Promise<TypeDefaultResponse & { course?: Course }> {
    return await customKy
      .post(url(baseURL, courses.create), {
        headers: getAuthHeader(token),
        json: payload,
      })
      .then((res) => res.json());
  }

  static async update(
    token: string,
    payload: TypeUpdatedUserInfo,
  ): Promise<
    TypeDefaultResponse & { course?: Course; incorrectFields?: string[] }
  > {
    const body = {
      updateData: {
        ...payload,
      },
    };

    return await customKy
      .patch(url(baseURL, courses.update, { id: payload._id }), {
        headers: getAuthHeader(token),
        json: body,
      })
      .then((res) => res.json());
  }

  static async delete(
    token: string,
    courseId: string,
  ): Promise<TypeDefaultResponse> {
    return await customKy
      .delete(url(baseURL, courses.delete, { id: courseId }), {
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async getCourseFileSignedUrl(
    token: string,
    filepath: string,
  ): Promise<TypeDefaultResponse & { url?: string }> {
    return await customKy
      .get(url(baseURL, courses.getCourseFileSignedUrl, { filepath }), {
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async deleteCourseFile(
    token: string,
    payload: {
      courseId: string;
      type: CourseFileTypes;
      chapterId?: string;
      lectureId?: string;
    },
  ): Promise<TypeDefaultResponse> {
    return await customKy
      .delete(
        url(baseURL, courses.deleteCourseFile, { id: payload.courseId }),
        {
          headers: getAuthHeader(token),
          json: payload,
        },
      )
      .then((res) => res.json());
  }

  static async getPersonalCertificateSignedUrl(
    token: string,
    courseId: string,
  ): Promise<
    TypeDefaultResponse & {
      url?: string;
    }
  > {
    return await customKy
      .get(
        url(baseURL, courses.getPersonalCertificateSignedUrl, { id: courseId }),
        {
          headers: getAuthHeader(token),
        },
      )
      .then((res) => res.json());
  }

  static async getSpecialistCourses(
    token: string,
    payload: {
      specialistUserId: string;
      specialistId?: string;
      page: number;
      limit: number;
    },
  ): Promise<TypeDefaultResponse & { courses?: Course[] }> {
    const params = new URLSearchParams();
    if (payload.specialistId) {
      params.append("specialistId", payload.specialistId);
    }

    return await customKy
      .get(
        url(baseURL, courses.getSpecialistCourses, {
          id: payload.specialistUserId,
          page: payload.page,
          limit: payload.limit,
        }),
        {
          headers: getAuthHeader(token),
        },
      )
      .then((res) => res.json());
  }

  static async getFilteredCourses(
    token: string,
    filters: TypeCoursesFilters,
  ): Promise<
    TypeDefaultResponse & {
      courses?: Course[];
      paramsAvailable: CoursesParamsAvailable;
    }
  > {
    return await customKy
      .post(
        url(baseURL, courses.getFilteredCourses, {
          page: filters.page,
          limit: filters.limit,
        }),
        {
          headers: getAuthHeader(token),
          json: { filters },
          timeout: 30000,
        },
      )
      .then((res) => res.json());
  }

  static async purchaseCourse(
    token: string,
    courseId: string,
    promocodeText?: string,
  ): Promise<
    TypeDefaultResponse & {
      course?: Course;
    }
  > {
    return await customKy
      .post(
        url(baseURL, courses.purchaseCourse, {
          id: courseId,
        }),
        {
          headers: getAuthHeader(token),
          timeout: 30000,
          json: { promocodeText },
        },
      )
      .then((res) => res.json());
  }

  static async getAllCourseFileSignedUrls(
    token: string,
    id: string,
  ): Promise<
    TypeDefaultResponse & {
      urls?: {
        coverImage?: string;
        promoVideo?: string;
        certificate?: string;
        chapters?: LectureFiles[];
      };
    }
  > {
    return await customKy
      .get(url(baseURL, courses.getAllCourseFileSignedUrls, { id }), {
        headers: getAuthHeader(token),
      })
      .then((res) => res.json());
  }

  static async cloneCourse(
    token: string,
    payload: {
      courseId: string;
      project?: string;
      createdBy?: string;
    },
  ): Promise<TypeDefaultResponse & { course?: Course }> {
    return await customKy
      .post(url(baseURL, courses.cloneCourse, { id: payload.courseId }), {
        headers: getAuthHeader(token),
        json: payload,
      })
      .then((res) => res.json());
  }

  /* Files actions */

  static async generateS3uploadUrlForFile(
    token: string,
    payload: {
      courseId: string;
      filename: string;
      filetype: string;
      type: CourseFileTypes;
      chapterId?: string;
      lectureId?: string;
    },
  ): Promise<
    TypeDefaultResponse & {
      url?: string;
      filepath?: string;
      type?: CourseFileTypes;
    }
  > {
    return await customKy

      .post(
        url(baseURL, courses.generateS3uploadUrlForFile, {
          id: payload.courseId,
        }),
        {
          headers: getAuthHeader(token),
          json: payload,
        },
      )
      .then((res) => res.json());
  }

  static async addFileToCourse(
    token: string,
    payload: {
      userId: string;
      courseId: string;
      filepath: string;
      type: CourseFileTypes;
      chapterId?: string;
      lectureId?: string;
    },
  ): Promise<
    TypeDefaultResponse & {
      url?: string;
      filepath?: string;
    }
  > {
    return await customKy
      .put(
        url(baseURL, courses.addFileToCourse, {
          id: payload.courseId,
        }),
        {
          headers: getAuthHeader(token),
          json: payload,
        },
      )
      .then((res) => res.json());
  }

  static async initiateFileMultipartUpload(
    token: string,
    payload: {
      courseId: string;
      filename: string;
      filetype: string;
      type: CourseFileTypes;
      chapterId?: string;
      lectureId?: string;
    },
  ): Promise<
    TypeDefaultResponse & {
      uploadId?: string;
      filepath?: string;
      type?: CourseFileTypes;
    }
  > {
    return await customKy

      .post(
        url(baseURL, courses.initiateFileMultipartUpload, {
          id: payload.courseId,
        }),
        {
          headers: getAuthHeader(token),
          json: payload,
        },
      )
      .then((res) => res.json());
  }

  static async generateFilePartUrl(
    token: string,
    payload: {
      filepath: string;
      uploadId: string;
      partNumber: number;
    },
  ): Promise<
    TypeDefaultResponse & {
      url?: string;
      partNumber?: number;
    }
  > {
    return await customKy

      .post(url(baseURL, courses.generateFilePartUrl), {
        headers: getAuthHeader(token),
        json: payload,
      })
      .then((res) => res.json());
  }

  static async completeFileMultipartUpload(
    token: string,
    payload: {
      courseId: string;
      filepath: string;
      uploadId: string;
      parts: { ETag: string; PartNumber: number }[];
      type: CourseFileTypes;
      chapterId?: string;
      lectureId?: string;
    },
  ): Promise<TypeDefaultResponse> {
    return await customKy
      .put(
        url(baseURL, courses.completeFileMultipartUpload, {
          id: payload.courseId,
        }),
        {
          headers: getAuthHeader(token),
          json: payload,
        },
      )
      .then((res) => res.json());
  }

  /* End files actions */
}
